import { useCallback } from 'react'
import { TrackEvent, useTracking } from '../../../apps/professional-app/fa/common/tracking'
import { DocumentSummaryInteractionResult } from '../../../models/ProcessDocument'
import { selectAccountId } from '../../../store/auth/authSlice'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import {
  selectDocumentSummaryAiAgentId,
  selectDocumentSummaryExecutionLoading,
  selectDocumentSummaryInteractionLoading,
  setDocumentSummaryAiAgentId,
  setDocumentSummaryInteractionLoading,
  setDocumentSummaryResult,
} from '../../../store/processDocument/documentSummary'
import { addMessage, selectLastUserChatMessage, updateStreamingMessage } from '../../../store/streamingChat/streamingChat'
import { StreamingAIAgentChat } from '../../AIAgent/Chat/StreamingAIAgentChat'
import { useStreamingAiAgent } from '../../AIAgent/hooks/useStreamingAiAgent'

export const DocumentSummaryChat = () => {
  const dispatch = useAppDispatch()
  const { tracked } = useTracking()

  const accountId = useAppSelector(selectAccountId)
  const aiAgentId = useAppSelector(selectDocumentSummaryAiAgentId)
  const executionLoading = useAppSelector(selectDocumentSummaryExecutionLoading)
  const interactionLoading = useAppSelector(selectDocumentSummaryInteractionLoading)
  const lastUserInteraction = useAppSelector(selectLastUserChatMessage)

  const onTextDelta = useCallback(
    (data: DocumentSummaryInteractionResult) => {
      const { message, summary } = data
      if (!message) {
        return
      }
      dispatch(updateStreamingMessage(message))
      if (summary) {
        dispatch(setDocumentSummaryResult({ summary }))
      }
    },
    [dispatch],
  )

  const onNewAiAgentId = useCallback(
    (newAiAgentId: string) => {
      dispatch(setDocumentSummaryAiAgentId(newAiAgentId))
    },
    [dispatch],
  )

  const { triggerInteractWithAiAgentStreaming } = useStreamingAiAgent<DocumentSummaryInteractionResult>({ onTextDelta, onNewAiAgentId })

  const handleResubmit = async () => {
    if (!aiAgentId || !lastUserInteraction) {
      return
    }
    dispatch(setDocumentSummaryInteractionLoading(true))
    try {
      const payload = { type: 'chat', query: lastUserInteraction.message }
      await triggerInteractWithAiAgentStreaming({ accountId, aiAgentId, payload })
    } catch (e) {
      console.error(e)
    } finally {
      dispatch(setDocumentSummaryInteractionLoading(false))
    }
  }

  const _handleDocumentSummaryChatInteraction = async (chatMessage: string) => {
    if (!aiAgentId) {
      return
    }
    dispatch(setDocumentSummaryInteractionLoading(true))
    dispatch(addMessage({ type: 'user', message: chatMessage }))
    try {
      await triggerInteractWithAiAgentStreaming({ accountId, aiAgentId, payload: { type: 'chat', query: chatMessage } })
    } catch (e) {
      console.error(e)
    } finally {
      dispatch(setDocumentSummaryInteractionLoading(false))
    }
  }

  const handleDocumentSummaryChatInteraction = tracked(_handleDocumentSummaryChatInteraction, {
    eventName: TrackEvent.DOCUMENT_SUMMARY_INTERACTED_WITH,
    eventDetails: { source: 'chat' },
  })

  return (
    <StreamingAIAgentChat
      handleSubmit={handleDocumentSummaryChatInteraction}
      isLoading={interactionLoading}
      disabled={executionLoading || interactionLoading}
      handleResubmit={handleResubmit}
    />
  )
}
